import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X } from 'lucide-react';

interface MultiSelectBadgesProps {
  label: string;
  description: string;
  items: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  placeholder: string;
  variant?: 'secondary' | 'destructive';
}

export function MultiSelectBadges({
  label,
  description,
  items,
  selectedValues,
  onAdd,
  onRemove,
  placeholder,
  variant = 'secondary',
}: MultiSelectBadgesProps) {
  // Create a map for quick label lookup
  const valueToLabel = new Map(items.map((item) => [item.value, item.label]));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Select
            value=""
            onValueChange={(value) => {
              if (value) onAdd(value);
            }}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-1">
          {selectedValues.map((value) => (
            <Badge key={value} variant={variant}>
              {valueToLabel.get(value) || value}
              <button
                type="button"
                className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(value);
                }}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                <span className="sr-only">Remove</span>
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
